import { useState, useEffect } from "react";
import { useAuth, useToast } from "../../components/Navbar";

export default function ShippingSettings() {
  const { token } = useAuth();
  const { addToast } = useToast();
  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newRule, setNewRule] = useState({ from_grams: "", to_grams: "", charge: "", per_kg_charge: "" });
  const [editingId, setEditingId] = useState(null);
  const [editRule, setEditRule] = useState({});

  useEffect(() => {
    fetch(`${API}/api/admin/shipping/settings`, { headers }).then(r => r.json()).then(setSettings);
    fetch(`${API}/api/admin/shipping/rules`, { headers }).then(r => r.json()).then(setRules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveSettings() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/shipping/settings`, {
        method: "PUT", headers, body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSettings(data);
      addToast("Shipping settings saved.");
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function addRule() {
    try {
      const res = await fetch(`${API}/api/admin/shipping/rules`, {
        method: "POST", headers,
        body: JSON.stringify({
          from_grams: Number(newRule.from_grams) || 0,
          to_grams: newRule.to_grams === "" ? null : Number(newRule.to_grams),
          charge: newRule.charge === "" ? null : Number(newRule.charge),
          per_kg_charge: newRule.per_kg_charge === "" ? null : Number(newRule.per_kg_charge),
          sort_order: rules.length,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setRules(prev => [...prev, data]);
      setNewRule({ from_grams: "", to_grams: "", charge: "", per_kg_charge: "" });
      addToast("Weight tier added.");
    } catch (err) {
      addToast(err.message, "error");
    }
  }

  async function deleteRule(id) {
    await fetch(`${API}/api/admin/shipping/rules/${id}`, { method: "DELETE", headers });
    setRules(prev => prev.filter(r => r.id !== id));
  }

  async function toggleRule(rule) {
    const is_active = rule.is_active ? 0 : 1;
    await fetch(`${API}/api/admin/shipping/rules/${rule.id}/toggle`, {
      method: "PATCH", headers, body: JSON.stringify({ is_active }),
    });
    setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, is_active } : r)));
  }

  function startEdit(rule) {
  setEditingId(rule.id);
  setEditRule({
    from_grams: rule.from_grams,
    to_grams: rule.to_grams ?? "",
    charge: rule.charge ?? "",
    per_kg_charge: rule.per_kg_charge ?? "",
  });
}

function cancelEdit() {
  setEditingId(null);
  setEditRule({});
}

async function saveEdit(id) {
  try {
    const res = await fetch(`${API}/api/admin/shipping/rules/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({
        from_grams: Number(editRule.from_grams) || 0,
        to_grams: editRule.to_grams === "" ? null : Number(editRule.to_grams),
        charge: editRule.charge === "" ? null : Number(editRule.charge),
        per_kg_charge: editRule.per_kg_charge === "" ? null : Number(editRule.per_kg_charge),
        is_active: rules.find(r => r.id === id)?.is_active ?? 1,
        sort_order: rules.find(r => r.id === id)?.sort_order ?? 0,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message);
    setRules(prev => prev.map(r => (r.id === id ? data : r)));
    setEditingId(null);
    addToast("Tier updated.");
  } catch (err) {
    addToast(err.message, "error");
  }
}

  if (!settings) return <div className="p-8 text-gray-500 text-sm">Loading…</div>;

  const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 text-sm";

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-xl font-bold text-gray-900 mb-6">Shipping Settings</h1>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8 space-y-4">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Method</label>
          <select className={inp} value={settings.method}
            onChange={e => setSettings(s => ({ ...s, method: e.target.value }))}>
            <option value="flat">Flat rate</option>
            <option value="weight">Weight-based tiers</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
            Default / flat charge ($)
          </label>
          <input className={inp} type="number" step="0.01" value={settings.default_charge}
            onChange={e => setSettings(s => ({ ...s, default_charge: e.target.value }))} />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!settings.free_shipping_enabled}
            onChange={e => setSettings(s => ({ ...s, free_shipping_enabled: e.target.checked }))} />
          <span className="text-sm text-gray-700">Enable free shipping above a threshold</span>
        </label>
        {settings.free_shipping_enabled === 1 || settings.free_shipping_enabled === true ? (
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
              Free shipping threshold ($)
            </label>
            <input className={inp} type="number" step="0.01" value={settings.free_shipping_threshold}
              onChange={e => setSettings(s => ({ ...s, free_shipping_threshold: e.target.value }))} />
          </div>
        ) : null}
        <button onClick={saveSettings} disabled={saving}
          className="px-5 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-semibold disabled:opacity-60">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {settings.method === "weight" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 text-sm mb-1">Weight Tiers</h2>
<p className="text-xs text-gray-400 mb-4">Every product is treated as 300g by default — price these tiers with that in mind.</p>
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase">
                <th className="py-2">From (g)</th><th>To (g)</th><th>Charge</th><th>Per kg</th><th>Active</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => {
                const isEditing = editingId === r.id;
                return (
                  <tr key={r.id} className="border-t border-gray-100">
                    {isEditing ? (
                      <>
                        <td className="py-2 pr-2">
                          <input className={inp} value={editRule.from_grams}
                            onChange={e => setEditRule(v => ({ ...v, from_grams: e.target.value }))} />
                        </td>
                        <td className="pr-2">
                          <input className={inp} placeholder="∞" value={editRule.to_grams}
                            onChange={e => setEditRule(v => ({ ...v, to_grams: e.target.value }))} />
                        </td>
                        <td className="pr-2">
                          <input className={inp} placeholder="—" value={editRule.charge}
                            onChange={e => setEditRule(v => ({ ...v, charge: e.target.value }))} />
                        </td>
                        <td className="pr-2">
                          <input className={inp} placeholder="—" value={editRule.per_kg_charge}
                            onChange={e => setEditRule(v => ({ ...v, per_kg_charge: e.target.value }))} />
                        </td>
                        <td>
                          <button onClick={() => toggleRule(r)}
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                            {r.is_active ? "Active" : "Off"}
                          </button>
                        </td>
                        <td className="whitespace-nowrap">
                          <button onClick={() => saveEdit(r.id)} className="text-emerald-600 text-xs font-semibold mr-3">Save</button>
                          <button onClick={cancelEdit} className="text-gray-400 text-xs font-semibold">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2">{r.from_grams}</td>
                        <td>{r.to_grams ?? "∞"}</td>
                        <td>{r.charge != null ? `$${Number(r.charge).toFixed(2)}` : "—"}</td>
                        <td>{r.per_kg_charge != null ? `$${Number(r.per_kg_charge).toFixed(2)}/kg` : "—"}</td>
                        <td>
                          <button onClick={() => toggleRule(r)}
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                            {r.is_active ? "Active" : "Off"}
                          </button>
                        </td>
                        <td className="whitespace-nowrap">
                          <button onClick={() => startEdit(r)} className="text-stone-600 text-xs font-semibold mr-3">Edit</button>
                          <button onClick={() => deleteRule(r.id)} className="text-red-500 text-xs font-semibold">Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="grid grid-cols-4 gap-2">
            <input className={inp} placeholder="From (g)" value={newRule.from_grams}
              onChange={e => setNewRule(r => ({ ...r, from_grams: e.target.value }))} />
            <input className={inp} placeholder="To (g, blank = ∞)" value={newRule.to_grams}
              onChange={e => setNewRule(r => ({ ...r, to_grams: e.target.value }))} />
            <input className={inp} placeholder="Flat charge" value={newRule.charge}
              onChange={e => setNewRule(r => ({ ...r, charge: e.target.value }))} />
            <input className={inp} placeholder="Per-kg charge" value={newRule.per_kg_charge}
              onChange={e => setNewRule(r => ({ ...r, per_kg_charge: e.target.value }))} />
          </div>
          <button onClick={addRule} className="mt-3 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold">
            Add Tier
          </button>
        </div>
      )}
    </div>
  );
}